import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentMembership } from '../services/api';

function CancelSubscription() {
  const [currentMembership, setCurrentMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMembership();
  }, []);

  const fetchMembership = async () => {
    try {
      setLoading(true);
      const data = await getCurrentMembership();
      setCurrentMembership(data);
    } catch (err) {
      console.log('No active membership found');
    } finally {
      setLoading(false);
    }
  };

  const isAnnual = currentMembership?.membership_type?.duration_months === 12;
  const isQuarterly = currentMembership?.membership_type?.duration_months === 3;
  const startDate = currentMembership?.current_period_start ? new Date(currentMembership.current_period_start + 'T00:00:00') : null;
  const daysSinceStart = startDate ? Math.floor((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const withinCancellationWindow = isAnnual && daysSinceStart <= 7;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!currentMembership || parseFloat(currentMembership.membership_type?.price) === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow rounded-lg p-8">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="mt-4 text-xl font-semibold text-gray-900">No Active Paid Subscription</h2>
              <p className="mt-2 text-gray-600">
                You don't have an active paid subscription to cancel.
              </p>
              <div className="mt-6">
                <Link
                  to="/membership"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  View Membership Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Cancel Subscription</h1>
              <p className="mt-1 text-gray-600">
                Information about canceling your {currentMembership.membership_type?.name}
              </p>
            </div>
            <Link
              to="/membership"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Membership
            </Link>
          </div>
        </div>

        {/* Current Subscription Status */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Current Subscription</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800">
                  {currentMembership.membership_type?.name}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>Plan Type: {isAnnual ? 'Annual' : isQuarterly ? 'Quarterly (3 months)' : 'Monthly'}</p>
                  <p>Started: {startDate ? startDate.toLocaleDateString() : 'N/A'}</p>
                  {currentMembership.current_period_end && (
                    <p>
                      {currentMembership.auto_renew ? 'Next Billing' : 'Expires'}: {new Date(currentMembership.current_period_end + 'T00:00:00').toLocaleDateString()}
                    </p>
                  )}
                  <p>Auto-Renewal: <span className="font-medium">{currentMembership.auto_renew ? 'Enabled' : 'Disabled'}</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Auto-Renewal Status Section */}
        {currentMembership.auto_renew ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.96-1.333-2.732 0L3.081 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Auto-Renewal is Currently Enabled</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Your subscription is set to automatically renew on {currentMembership.current_period_end ? new Date(currentMembership.current_period_end + 'T00:00:00').toLocaleDateString() : 'your next billing date'}.</p>
                  <p className="mt-3 font-medium">To stop automatic renewal:</p>
                  <ol className="mt-2 ml-4 list-decimal list-outside space-y-1">
                    <li>Go to <Link to="/billing-settings" className="underline font-medium">Billing Settings</Link></li>
                    <li>Toggle the Auto-Renewal switch to OFF</li>
                    <li>Your subscription will remain active until the end of your current billing period</li>
                  </ol>
                  <p className="mt-3 text-xs">
                    Note: Disabling auto-renewal does not cancel your current subscription. You'll retain access until your subscription expires.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">No Recurring Charges</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Auto-renewal is currently disabled. Your subscription will not renew automatically.</p>
                  <p className="mt-2">
                    Your subscription will remain active until {currentMembership.current_period_end ? new Date(currentMembership.current_period_end + 'T00:00:00').toLocaleDateString() : 'the end of your billing period'}, after which you'll need to manually renew if you wish to continue.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Policy */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Cancellation Policy</h2>

          <div className="space-y-4">
            {/* Annual Plan Policy */}
            {isAnnual && (
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Annual Plan Policy</h3>
                <div className="text-sm text-gray-600 space-y-2">
                  {withinCancellationWindow ? (
                    <>
                      <p className="text-green-600 font-medium">
                        ✓ You are within the 7-day cancellation window (started {daysSinceStart} day{daysSinceStart !== 1 ? 's' : ''} ago)
                      </p>
                      <p>Annual subscriptions can be cancelled with a full refund within 7 days of purchase.</p>
                      <p>To request a cancellation and refund, please contact our support team (details below).</p>
                    </>
                  ) : (
                    <>
                      <p className="text-orange-600 font-medium">
                        ⓘ Your 7-day cancellation window has passed (started {daysSinceStart} days ago)
                      </p>
                      <p>Annual subscriptions can only be cancelled with a full refund within 7 days of purchase.</p>
                      <p>After 7 days, the subscription remains active for the full annual period. You may disable auto-renewal to prevent future charges.</p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Quarterly Plan Policy */}
            {isQuarterly && (
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Quarterly Plan Policy</h3>
                <div className="text-sm text-gray-600 space-y-2">
                  <p>Quarterly (3-month) subscriptions are non-refundable and cannot be cancelled mid-term.</p>
                  <p>Your subscription will remain active for the full 3-month period.</p>
                  <p>You may disable auto-renewal to prevent charges for the next quarter.</p>
                </div>
              </div>
            )}

            {/* General Information */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Important Information</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Disabling auto-renewal does not cancel your current subscription access</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>You will continue to have access until the end of your current billing period</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>No partial refunds are available for unused time within a billing period</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="bg-white shadow rounded-lg p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h2>
          <p className="text-sm text-gray-600 mb-4">
            If you have questions about cancellation or need to request a refund (if eligible), please contact our support team.
          </p>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Contact Support</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <span className="font-medium">Email:</span>{' '}
                <a href="mailto:support@research-triangle.ai" className="text-blue-600 hover:text-blue-800 underline">
                  support@research-triangle.ai
                </a>
              </p>
              <p className="mt-4 text-xs text-gray-500">
                When contacting support, please include:
              </p>
              <ul className="ml-4 list-disc text-xs text-gray-500 space-y-1">
                <li>Your account email address</li>
                <li>Subscription type ({currentMembership.membership_type?.name})</li>
                <li>Reason for cancellation</li>
                <li>Purchase date ({startDate ? startDate.toLocaleDateString() : 'N/A'})</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center space-x-4">
            <Link
              to="/billing-settings"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Manage Auto-Renewal
            </Link>
            <Link
              to="/membership"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to Membership
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CancelSubscription;